import { useCallback, useRef, useState } from 'react';
import { SparkIcon } from '../SparkIcon';
import { Spinner } from '../Spinner';
import { TagPill } from '../Tag/TagPill';
import { TooltipTrigger } from '../Tooltip/TooltipTrigger';
import type { UploadAreaState, UploadAreaContentDirection, UploadAreaProps } from './UploadArea.types';
import './UploadArea.css';

function UploadAreaIcon({ state, aiGenerated }: { state: UploadAreaState; aiGenerated: boolean }) {
  if (state === 'in-progress') {
    return <SparkIcon slug="loader" size="large" className="spark-upload-area-icon-spin" />;
  }
  if (state === 'success') {
    return <SparkIcon slug={aiGenerated ? 'file-sparkles' : 'description'} size="large" />;
  }
  if (state === 'failed') {
    return <SparkIcon slug="file-close" size="large" />;
  }
  return <SparkIcon slug="file-upload" size="large" />;
}

function defaultHelperText(state: UploadAreaState): string {
  if (state === 'failed') {
    return 'Check file format and try again';
  }
  return 'Helper text';
}

function UploadAreaActionButton({
  label,
  icon,
  onClick,
  danger = false,
}: {
  label: string;
  icon: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <TooltipTrigger text={label}>
      <button
        type="button"
        className={['spark-upload-area-remove', danger ? 'spark-upload-area-remove-danger' : '']
          .filter(Boolean)
          .join(' ')}
        aria-label={label}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
      >
        <SparkIcon slug={icon} size="small" />
      </button>
    </TooltipTrigger>
  );
}

export function UploadArea({
  state = 'default',
  contentDirection = 'left',
  showHelperText = false,
  helperText,
  title = 'Click or drag file to upload',
  subtitle = 'Support for CSV or JSON files only',
  fileName = 'azurefile_name.xml',
  fileSize = '500 KB',
  fileSizeLoading = false,
  progress = 43,
  onRemove,
  onInfo,
  infoIcon = 'circle-info',
  aiGenerated = false,
  aiPillLabel = 'AI-generated',
  allowRemoveInProgress = false,
  onFilesSelected,
  accept,
  multiple = true,
  fullWidth = false,
  className = '',
}: UploadAreaProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const isOutcome = state === 'success' || state === 'failed';
  const isInteractive = state === 'default' || state === 'hover' || state === 'click-on';
  const showInfoButton =
    Boolean(onInfo) && !aiGenerated && (isOutcome || state === 'in-progress');
  const showAiPill = aiGenerated && (isOutcome || state === 'in-progress');
  // AI cards open the details modal from an eye action button (Figma 5132:15791)
  // instead of the pill, which stays display-only.
  const showViewDetails =
    Boolean(onInfo) && aiGenerated && (isOutcome || state === 'in-progress');
  const showRemove =
    Boolean(onRemove) && (isOutcome || (allowRemoveInProgress && state === 'in-progress'));
  const showActions = showInfoButton || showViewDetails || showRemove;

  const visualState: UploadAreaState = dragOver && isInteractive ? 'hover' : state;

  const resolvedHelperText = helperText ?? defaultHelperText(state);

  const primaryText =
    state === 'in-progress'
      ? fileName || 'Uploading File'
      : state === 'failed'
        ? 'Upload Failed'
        : state === 'success'
          ? fileName
          : title;
  const showSizeSpinner = state === 'success' && fileSizeLoading;
  const secondaryText =
    state === 'in-progress'
      ? `${Math.round(progress * 100) / 100}%`
      : state === 'success'
        ? showSizeSpinner
          ? undefined
          : fileSize
        : state === 'failed'
          ? undefined
          : subtitle;

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      onFilesSelected?.(Array.from(files));
    },
    [onFilesSelected],
  );

  const handleZoneClick = useCallback(() => {
    if (isInteractive) {
      fileInputRef.current?.click();
    }
  }, [isInteractive]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isInteractive && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        fileInputRef.current?.click();
      }
    },
    [isInteractive],
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (isInteractive) setDragOver(true);
    },
    [isInteractive],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (isInteractive) setDragOver(true);
    },
    [isInteractive],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
    },
    [],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      if (isInteractive) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [isInteractive, handleFiles],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [handleFiles],
  );

  const rootClasses = ['spark-upload-area', fullWidth ? 'spark-upload-area-full-width' : '', className]
    .filter(Boolean)
    .join(' ');

  const zoneClasses = [
    'spark-upload-area-zone',
    `spark-upload-area-zone-${visualState}`,
    contentDirection === 'center' ? 'spark-upload-area-zone-center' : 'spark-upload-area-zone-left',
    isOutcome || showActions ? 'spark-upload-area-zone-outcome' : '',
    aiGenerated ? 'spark-upload-area-zone-ai' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const copyClasses = [
    'spark-upload-area-copy',
    contentDirection === 'center' && isInteractive ? 'spark-upload-area-copy-center' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClasses}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleInputChange}
        className="spark-upload-area-hidden-input"
        tabIndex={-1}
        aria-hidden="true"
      />
      <div
        className={zoneClasses}
        role={isInteractive ? 'button' : undefined}
        tabIndex={state === 'default' ? 0 : undefined}
        onClick={handleZoneClick}
        onKeyDown={handleKeyDown}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="spark-upload-area-content">
          <div className="spark-upload-area-icon" aria-hidden="true">
            <UploadAreaIcon state={visualState} aiGenerated={aiGenerated} />
          </div>
          <div className={copyClasses}>
            {isOutcome ? (
              <TooltipTrigger
                text={primaryText}
                placement="top"
                className="spark-upload-area-title-tooltip"
              >
                <p
                  className={[
                    'spark-upload-area-title',
                    state === 'failed' ? 'spark-upload-area-title-failed' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {primaryText}
                </p>
              </TooltipTrigger>
            ) : (
              <p className="spark-upload-area-title">
                {primaryText}
              </p>
            )}
            {secondaryText || showAiPill || showSizeSpinner ? (
              <div className="spark-upload-area-meta">
                {showSizeSpinner ? (
                  <Spinner size="small" aria-label="Loading file details" />
                ) : secondaryText ? (
                  state === 'success' ? (
                    // The size can be ellipsized when the pill crowds the row —
                    // hover reveals the full text.
                    <TooltipTrigger
                      text={secondaryText}
                      placement="top"
                      className="spark-upload-area-size-tooltip"
                    >
                      <p className="spark-upload-area-subtitle">{secondaryText}</p>
                    </TooltipTrigger>
                  ) : (
                    <p className="spark-upload-area-subtitle">{secondaryText}</p>
                  )
                ) : null}
                {showAiPill ? (
                  <TagPill label={aiPillLabel} endIconSlug="circle-info" />
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        {showActions ? (
          <div className="spark-upload-area-actions">
            {showInfoButton ? (
              <UploadAreaActionButton
                label="More info"
                icon={infoIcon}
                onClick={() => onInfo?.()}
              />
            ) : null}
            {showViewDetails ? (
              <UploadAreaActionButton
                label="View details"
                icon="eye"
                onClick={() => onInfo?.()}
              />
            ) : null}
            {showRemove ? (
              <UploadAreaActionButton
                label="Remove file"
                icon="trash"
                danger={state === 'failed'}
                onClick={() => onRemove?.()}
              />
            ) : null}
          </div>
        ) : null}
      </div>
      {showHelperText ? <p className="spark-upload-area-helper">{resolvedHelperText}</p> : null}
    </div>
  );
}
